import React, { createContext, useState, useEffect, useContext, useCallback, useRef } from 'react';
import connectionService, { ConnectionErrorType } from '../services/connectionService';

const ConnectionContext = createContext(null);

/**
 * Connection Provider
 * Monitors global connectivity status and provides retry mechanisms
 * Implements debouncing with failure threshold to prevent false positives
 */
export const ConnectionProvider = ({ children }) => {
  const [connectionStatus, setConnectionStatus] = useState({
    isConnected: true, // Assume connected initially
    isChecking: false,
    errorType: null,
    errorMessage: null,
    lastChecked: null,
    retryCount: 0
  });

  const [showBanner, setShowBanner] = useState(false);
  const checkIntervalRef = useRef(null);
  const retryTimeoutRef = useRef(null);

  // Debouncing state: require multiple consecutive failures before showing banner
  const failureCountRef = useRef(0);
  const consecutiveSuccessesRef = useRef(0);
  const FAILURE_THRESHOLD = 2; // Require 2 consecutive failures to show banner

  /**
   * Get check interval based on connection stability (hysteresis)
   * More consecutive successes = longer intervals between checks
   */
  const getCheckInterval = useCallback(() => {
    const successes = consecutiveSuccessesRef.current;
    if (successes >= 5) return 60000;   // 1 minute after 5+ consecutive successes
    if (successes >= 3) return 45000;   // 45s after 3+ consecutive successes
    return 30000;  // 30s by default
  }, []);

  /**
   * Perform connection check with debouncing
   * Requires FAILURE_THRESHOLD consecutive failures before showing banner
   */
  const checkConnection = useCallback(async () => {
    setConnectionStatus(prev => ({ ...prev, isChecking: true }));

    try {
      const result = await connectionService.checkConnection();

      if (!result.isConnected) {
        // Increment failure count
        failureCountRef.current += 1;
        consecutiveSuccessesRef.current = 0;

        console.log(`[ConnectionContext] Connection check failed (${failureCountRef.current}/${FAILURE_THRESHOLD})`);

        // Only show banner after reaching threshold
        if (failureCountRef.current >= FAILURE_THRESHOLD) {
          setShowBanner(true);
        }

        setConnectionStatus(prev => ({
          isConnected: false,
          isChecking: false,
          errorType: result.errorType || null,
          errorMessage: result.details?.message || null,
          lastChecked: new Date(),
          retryCount: prev.retryCount
        }));

        return false;
      } else {
        // Connection successful - reset failure count
        failureCountRef.current = 0;
        consecutiveSuccessesRef.current += 1;
        setShowBanner(false);

        setConnectionStatus(prev => ({
          isConnected: true,
          isChecking: false,
          errorType: null,
          errorMessage: null,
          lastChecked: new Date(),
          retryCount: 0
        }));

        return true;
      }

    } catch (error) {
      console.error('[ConnectionContext] Check failed:', error);
      failureCountRef.current += 1;
      consecutiveSuccessesRef.current = 0;

      // Only show banner after reaching threshold
      if (failureCountRef.current >= FAILURE_THRESHOLD) {
        setShowBanner(true);
      }

      setConnectionStatus(prev => ({
        ...prev,
        isChecking: false,
        isConnected: false,
        errorType: ConnectionErrorType.UNKNOWN,
        errorMessage: 'Error al verificar la conexión'
      }));

      return false;
    }
  }, []); // ✅ FIX: Empty deps - uses refs for mutable state, constants don't need deps

  /**
   * Retry connection with exponential backoff
   */
  const retryConnection = useCallback(async () => {
    setConnectionStatus(prev => ({
      ...prev,
      retryCount: prev.retryCount + 1
    }));

    const isConnected = await checkConnection();

    if (!isConnected) {
      // Exponential backoff: 2s, 4s, 8s, max 30s
      const delay = Math.min(2000 * Math.pow(2, connectionStatus.retryCount), 30000);

      retryTimeoutRef.current = setTimeout(() => {
        retryConnection();
      }, delay);
    }

    return isConnected;
  }, [checkConnection, connectionStatus.retryCount]);

  /**
   * Manual retry triggered by user
   */
  const manualRetry = useCallback(async () => {
    // Clear any existing retry timeout
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }

    setConnectionStatus(prev => ({
      ...prev,
      retryCount: 0
    }));

    return await checkConnection();
  }, [checkConnection]);

  /**
   * Dismiss connection banner
   */
  const dismissBanner = useCallback(() => {
    setShowBanner(false);
  }, []);

  /**
   * Initialize connection monitoring
   */
  useEffect(() => {
    // Initial check
    checkConnection();

    // Listen to browser online/offline events
    const handleOnline = () => {
      console.log('[ConnectionContext] Browser online event');
      failureCountRef.current = 0; // Reset failure count on browser online event
      checkConnection();
    };

    const handleOffline = () => {
      console.log('[ConnectionContext] Browser offline event');
      failureCountRef.current = FAILURE_THRESHOLD; // Immediately show banner
      consecutiveSuccessesRef.current = 0;
      setConnectionStatus(prev => ({
        ...prev,
        isConnected: false,
        errorType: ConnectionErrorType.NETWORK_ERROR,
        errorMessage: 'Sin conexión a Internet'
      }));
      setShowBanner(true);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Setup periodic health check with dynamic interval (hysteresis)
    const setupPeriodicCheck = () => {
      // Clear existing interval if any
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }

      const interval = getCheckInterval();
      console.log(`[ConnectionContext] Setting up periodic check with ${interval}ms interval`);

      checkIntervalRef.current = setInterval(() => {
        // Only check if we believe we're connected
        // If disconnected, retryConnection handles the retry logic
        if (connectionStatus.isConnected) {
          checkConnection().then(() => {
            // After check completes, re-evaluate interval based on new success count
            const newInterval = getCheckInterval();
            if (newInterval !== interval) {
              setupPeriodicCheck();
            }
          });
        }
      }, interval);
    };

    setupPeriodicCheck();

    // Cleanup
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);

      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }

      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
    };
  // ✅ FIX: Removed checkConnection and getCheckInterval from deps to prevent infinite loop
  // These are useCallback functions that are stable - including them causes unnecessary re-runs
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectionStatus.isConnected, FAILURE_THRESHOLD]);

  // ✅ FIX: Only include state values in useMemo dependencies
  // Functions wrapped in useCallback are stable and don't need to be dependencies
  // Including them in deps can cause infinite re-render loops
  const value = React.useMemo(() => ({
    ...connectionStatus,
    showBanner,
    checkConnection,
    retryConnection,
    manualRetry,
    dismissBanner
  }), [connectionStatus, showBanner]); // Only state values as dependencies

  return (
    <ConnectionContext.Provider value={value}>
      {children}
    </ConnectionContext.Provider>
  );
};

/**
 * Hook to use connection context
 */
export const useConnection = () => {
  const context = useContext(ConnectionContext);
  if (context === undefined || context === null) {
    throw new Error('useConnection must be used within a ConnectionProvider');
  }
  return context;
};

export default ConnectionContext;
